<template>
  <card-wrapper v-if="coupon"
    max-height="none"
    :forced-height="106"
    :forced-width="284"
    :min-width="284"
    :clearActive="clearCoupon"
    :onClick="openConfig"
    :image="getImage()"
    :isActive="isActive(coupon) && !immutable"
    id="promotionRewardsCard"
  >
    <template v-slot:textBody>
      <div class="promo-card-body">
        <div class="header" :class="{'error-title': isDisabled}">
          {{ coupon.name || coupon.code }}
        </div>

        <div v-if="!isPurchasable && (!hasRules || !hasRules.length)" class="subheader no-limitation">
          • {{ $vuetify.t('$vuetify.account.rewards.noLimitation') }}
        </div>
        <div v-if="isPurchasable || (hasRules && hasRules.length)" class="rules-list" :title="rule">
          <ul class="bullet-list">
            <!-- A purchasable coupon's price is one of its rule lines, styled like the rest:
                 the card is a fixed 106px, so the old description-slot price hung off it.
                 Price first, since a rule line that's too long gets cut with "…". -->
            <li v-if="isPurchasable" class="subheader rule-item">
              • <common-price :init-charges="coupon.charges.map(charge => ({ ...charge, price: charge.value }))" /> to add to account
            </li>
            <li v-for="rule in hasRules" :key="rule" class="subheader rule-item">
              • {{rule}}
            </li>
          </ul>
        </div>
        <div v-if="coupon.eligibilityResult && coupon.eligibilityResult.expirationDescription" class="offer-expiry">
          {{coupon.eligibilityResult.expirationDescription}}
        </div>
        <div v-else class="offer-expiry">
          {{ $vuetify.t('$vuetify.account.rewards.noExpiration') }}
        </div>
      </div>
  	</template>

    <template v-if="showItems && coupon.orderItem && coupon.orderItem.childItemList" v-slot:description>
      <div v-for="relation in coupon.orderItem.childItemList || []">
        <v-layout v-for="item in relation.childItemList" :key="item.code" class="menuItemContainer">
          <v-flex xs10>
            <div class="itemName">
              {{ item.qty || 1 }} x {{ findDisplayText(item || item) }}
            </div>

            <ul class="cart-list">
              <li v-for="(config, index) in fetchConfigItems(item)" :key="index">
                {{ config.qty && config.qty > 1 ? `${config.qty} x ` : '' }}
                {{ config.name }}
                <span v-if="config.price" class="caption grey--text">
                  {{config.price | currency }}
                </span>
              </li>
            </ul>
          </v-flex>

          <v-flex xs2>
            <span v-if="'itemPrice' in item && 'unitDiscount' in item && item.unitDiscount < 0" class="mr-2 caption" style="text-decoration: line-through;">
              <span class="red--text">
                {{ (item.itemPrice - item.unitDiscount) | currency}}
              </span>
            </span>

            <template v-if="'itemPrice' in item">
              <template v-if="item.itemPrice">
                {{ item.itemPrice | currency }}
              </template>
            </template>
            <common-price v-else class="mr-2" :init-charges="item.charges || []" />
          </v-flex>
        </v-layout>
      </div>
    </template>
  </card-wrapper>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from 'vuex';
import { matchTranslation } from "@/services/util.service"
import { formatDiscountCap } from "@/services/currency.service"
import get from 'lodash/get';


import CommonPrice from "@/components/Common/CommonPrice"
import CardWrapper from "@/components/Wrappers/CardWrapper"
import HeaderBarApps from '@/components/ExternalAppsLinks'

export default {
  components: { CardWrapper, CommonPrice },
  extends: HeaderBarApps,

  props: {
    coupon: {
      type: Object,
      default: null
    },

    allowConfig: {
      type: Boolean,
      default: false
    },

    showItems: {
      type: Boolean,
      default: false
    },

    immutable: {
      type: Boolean,
      default: false
    },

    maxWidth: {
      type: String,
      default: '350px'
    }
  },

  computed: {
    isDisabled() {
      return !get(this.coupon, 'eligibilityResult.isEligible', true) && !this.isPurchasable
    },
    isPurchasable() {
      return !!this.coupon.charges && !this.coupon.uuid
    },

    hasRules() {
      const rules = get(this.coupon, 'eligibilityResult.ruleDescriptions', []) || []
      // DEV-10491: a capped percentage coupon lists its cap first, same as CouponCard.
      const cap = formatDiscountCap(get(this.coupon, 'maxDiscountAmount'), this.getCurrencyCode)
      return cap ? [this.$vuetify.t('$vuetify.account.rewards.maxDiscountOf', cap), ...rules] : rules
    },

    ...mapGetters([ 'getStore', 'getExistingOrder', 'getActiveCoupon', 'isLoggedIn', 'getFinalChargeFromCharges', 'getOrderType', 'getUserID', 'getCurrencyCode' ])
  },

  methods: {
    fireCouponEvent(eventName, coupon) {
      try {
        const payload = {
          coupon_code: coupon.code || '',
          coupon_name: coupon.name || '',
          order_type: this.getOrderType || '',
          store_id: String((this.getStore && this.getStore.storeId) || ''),
        }
        if (this.getUserID) payload.user_id = String(this.getUserID)
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ event: eventName, ...payload })
        if (typeof window.gtag === 'function') window.gtag('event', eventName, payload)
      } catch (e) { console.warn(eventName + ' tracking error', e) }
    },

    getImage() {
      if (this.coupon.imageUrl)
        return `${process.env.VUE_APP_API_IMAGE}${this.coupon.imageUrl}`
      return null
    },

    openLoginDialog() {
      this.showSnack({
        text: this.$vuetify.t('$vuetify.account.rewards.loginPrompt', [this.coupon.name]),
        color: 'secondary'
      })

      this.setLoginDialog(true)
    },

    addToCart(activeCoupon) {
      this.fireCouponEvent('coupon_added', this.coupon)
      this.SET_ACTIVE_COUPON({
        code: this.coupon.code,
        name: this.coupon.name,
        description: this.coupon.description,
        imageUrl: this.coupon.imageUrl,
        uuid: this.coupon.uuid,
        purchase: !!this.coupon.charges,
        charges: this.coupon.charges,
        orderItem: null
      })

      this.showSnack({
        text: this.$vuetify.t('$vuetify.common.addedToCart', [this.coupon.name]),
        color: 'secondary'
      })

      this.fetchShoppingCart()
    },

    checkCouponValididity(activeCoupon) {
      if (!this.allowConfig)
        return false

      if (this.isDisabled) {
        this.showSnack({
          text: this.$vuetify.t('$vuetify.account.rewards.inEligible'),
          color: 'error'
        })
        return false
      }

      // If there is an active coupon, the coupon must be the active one to configure it
      if (activeCoupon && activeCoupon.code !== this.coupon.code) {
        this.showSnack({
          text: 'Please remove your active coupon first',
          color: 'error'
        })
        return false
      }

      if (this.getExistingOrder.length) {
        // Cannot add a coupon if a previous sub order already used one
        if (!!this.getExistingOrder.find(order => order.coupons && order.coupons.length)) {
          this.showSnack({
            text: 'A previous order already used a coupon',
            color: 'error'
          })
          return false
        }
      }

      return true
    },

    openConfig() {
      const activeCoupon = this.getActiveCoupon[0]
      if (!this.checkCouponValididity(activeCoupon))
        return
      this.fireCouponEvent('coupon_opened', this.coupon)

      if (this.isPurchasable) {
        if (!this.isLoggedIn) {
          this.openLoginDialog()
        } else {
          window.open(this.buildCouponUrl, '_self')
        }
      } else if (('menuItem' in this.coupon || 'orderItem' in this.coupon) || !this.isLoggedIn) {
        // If there's no config but the user is not logged in, log in to check if the user has the coupon
        this.setCouponItemDialog(JSON.parse(JSON.stringify(activeCoupon || this.coupon)))
      } else if (activeCoupon) {
        // If the coupon has no config and is already active, no need to re-add it
        this.showSnack({
          text: this.$vuetify.t('$vuetify.account.rewards.alreadyInCart', [this.coupon.name]),
          color: 'secondary'
        })
      } else {
        this.setCouponItemDialog(JSON.parse(JSON.stringify(activeCoupon || this.coupon)))
        // if the coupon doesn't have any configuration, we can skip opening the config
        // this.addToCart(activeCoupon)
      }
    },

    isActive(coupon) {
      const active = get(this.getActiveCoupon, '[0]')

      // If the coupon has a uuid, using that to identify it is more accurate
      // But non instance versions will not. So fall back on matching the code
      if (!!active) {
        const couponIdentifier = coupon.uuid || coupon.code
        const activeIdentifier = active.uuid || active.code

        return couponIdentifier == activeIdentifier
      }

      return false
    },

    clearCoupon() {
      if (this.$store.getters['groupOrder/isInGroupOrder']) {
        this.$store.dispatch('groupOrder/removeCoupon')
          .then(() => this.$store.dispatch('groupOrder/fetchGroupState'))
          .catch((e) => console.error('[clearCoupon group]', e))
        return
      }
      this.removeCoupon(null)
      this.fetchShoppingCart()
    },

    fetchConfigItems(item) {
      let configItems = []
      // History items use standard childItemList wrapper structure. Fallback is normal case
      const list = get(item, 'itemAttributeList', [])

      list.map(option => {
        option.attrValueList.forEach(opt => {
          configItems.push(opt)
        })
      })

      return configItems.filter(Boolean)
    },

    findDisplayText(item) {
      const translations = get(item, 'nameTranslation.translations', null)

      return matchTranslation(translations) || item.name || item.value || ''
    },

    ...mapActions([ 'fetchShoppingCart', 'setCouponItemDialog', 'removeCoupon', 'showSnack' ]),
    ...mapMutations([ 'SET_ACTIVE_COUPON', 'setLoginDialog' ])
  }
}
</script>

<style scoped>
  .bullet-list {
    margin: 0;
    margin-top: 5px;
    padding-left: 0px;
  }

  .rule-item {
    font-weight: bold;
    font-size: 11px;
    margin-bottom: 2px;
  }
  
  .no-limitation {
    font-size: 11px;
  }
  
  .menuItemContainer {
    margin-top: 20px;
  }
  .itemName {
    font-size: 16px;
    color: var(--v-primary-base);
  }
  .itemPrice {
    font-size: 14px;
    color: var(--v-secondary-base);
  }
  .error-title {
    color: #D8000C
  }
  .promo-card-body {
    position: relative;
    min-height: 100%; 
    padding-bottom: 10px;
  }

  .offer-expiry {
    font-size: 11px;
    color: #757575;
    position: absolute;
    left: 0;
    bottom: 0;
    margin: 0;
    text-align: left;
    letter-spacing: 0.2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .expiration-description-bottom {
    position: absolute;
    left: 0;
    bottom: 0;
    color: #898a8d;
    margin: 0;
    text-align: left;
    letter-spacing: 0.2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 10px !important;
  }
</style>